'use client';

import { Address, Alert, AlertBody, Button, Heading, Icon, type IconNames, TextPassage, Tooltip } from '@petsmart-ui/sparky';
import classNames from 'classnames';
import { type SubmitEventHandler, useEffect, useRef } from 'react';
import type { UpdateShippingAddressRequest } from '@libs/api-proxy-client';
import { type AccountPaymentsSuccessResponse, getCreditCardIcon } from '@libs/common-next';
import { getCardBrandName } from './cardBrand';
import { CVV_CHALLENGE_COPY, CVV_CONTAINER_ID } from './constants';
import { useStoredCardCvv } from './useStoredCardCvv';
const componentClass = 'cvv-challenge';
interface CvvFormProps {
  card: AccountPaymentsSuccessResponse;
  address?: UpdateShippingAddressRequest;
  isSubmitting: boolean;
  errorMessage?: string;
  onSubmit: (encryptedSecurityCode: string) => Promise<void>;
  onCvvChange: () => void;
}
export function CvvForm({
  card,
  address,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCvvChange
}: Readonly<CvvFormProps>) {
  const {
    component,
    state
  } = useStoredCardCvv(card, CVV_CONTAINER_ID);
  const cvvFieldRef = useRef<HTMLDivElement>(null);
  const brandName = getCardBrandName(card.cardType);
  const encryptedSecurityCode = state?.data?.paymentMethod?.encryptedSecurityCode;

  // Hold focus where the CVV input will appear, so the drawer's focus lock doesn't move it
  // to the info icon while Adyen loads. The input takes focus once Adyen is ready.
  useEffect(() => {
    cvvFieldRef.current?.focus();
  }, []);

  // A server error belongs to the value that was submitted, so clear it as soon as the CVV changes.
  useEffect(() => {
    onCvvChange();
  }, [encryptedSecurityCode, onCvvChange]);
  const handleSubmit: SubmitEventHandler<HTMLFormElement> = async (event): Promise<void> => {
    event.preventDefault();
    if (!state?.isValid || !encryptedSecurityCode) {
      component?.showValidation();
      return;
    }
    await onSubmit(encryptedSecurityCode);
  };
  return <form className={`${componentClass}__form`} onSubmit={handleSubmit} noValidate data-testid="cvv-challenge-form">
            <Alert className={`${componentClass}__alert`} variant="info" iconName="information-circle-fill" dismissible={false}>
                <AlertBody>
                    <div className={`${componentClass}__alert-body`}>
                        <Heading tagName="h2" size="body-bold">
                            {CVV_CHALLENGE_COPY.cvvAlertTitle}
                        </Heading>
                        <TextPassage capLinelength={false}>
                            {CVV_CHALLENGE_COPY.cvvAlertBody(brandName, card.lastFour)}
                        </TextPassage>
                    </div>
                </AlertBody>
            </Alert>

            {address && <div className={`${componentClass}__address`} data-testid="cvv-challenge-address">
                    <Heading tagName="h3" size="body-bold">
                        {CVV_CHALLENGE_COPY.newAddressLabel}
                    </Heading>
                    <Address addressName={`${address.firstName} ${address.lastName}`} street={address.line1} extendedAddress={address.line2} city={address.city} state={address.state} postalCode={address.postalCode} orientation="horizontal" showAddressName />
                </div>}

            <div className={`${componentClass}__card`} data-testid="cvv-challenge-card">
                <Icon name={getCreditCardIcon(card.cardType) as IconNames} size="2xl" />
                <TextPassage>
                    {brandName} ••••{card.lastFour}
                </TextPassage>
            </div>

            <div className={classNames(`${componentClass}__cvv`, {
      [`${componentClass}__cvv--error`]: !!errorMessage
    })}>
                <div id={CVV_CONTAINER_ID} ref={cvvFieldRef} tabIndex={-1} className={`${componentClass}__cvv-field`} data-cs-mask />
                <Tooltip className={`${componentClass}__cvv-tooltip`} align="left" data-testid="cvv-challenge-tooltip">
                    {CVV_CHALLENGE_COPY.cvvTooltip}
                </Tooltip>
            </div>

            {errorMessage && <span className={`${componentClass}__error adyen-checkout-contextual-text--error`} role="alert" data-testid="cvv-challenge-error">
                    {errorMessage}
                </span>}

            <Button type="submit" variant="primary" size="lg" fullWidth text={CVV_CHALLENGE_COPY.submitCta} loading={isSubmitting} disabled={isSubmitting || !component} data-testid="cvv-challenge-submit" />
        </form>;
}